import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { InitializationProgressComponent } from '../../../components/initialization-progress/initialization-progress.component';
import { DisplayDecorator } from '../../../decorators/display-decorator';
import { InitializationInstanceToken } from '../../../interfaces/initialization.interface';
import { ODataModel, ODataModelChangeEvent } from '../../../interfaces/odata-model.interface';
import { DropdownModel } from '../../../models/complex/dropdown.model';
import { RadioModel } from '../../../models/complex/radio.model';
import { BooleanModelOptions } from '../../../models/options/boolean-model-options';
import { DateModelOptions } from '../../../models/options/date-model-options';
import { DropdownModelOptions } from '../../../models/options/dropdown-model-options';
import { DurationModelOptions } from '../../../models/options/duration-model-options';
import { NumberModelOptions } from '../../../models/options/number-model-options';
import { RadioModelOptions } from '../../../models/options/radio-model-options';
import { TextModelOptions } from '../../../models/options/text-model-options';
import { TimeModelOptions } from '../../../models/options/time-model-options';
import { TreeViewModelOptions } from '../../../models/options/treeview-model-options';
import { BooleanModel } from '../../../models/primitive/boolean.model';
import { DateModel } from '../../../models/primitive/date.model';
import { DurationModel } from '../../../models/primitive/duration.model';
import { NumberModel } from '../../../models/primitive/number.model';
import { TextModel } from '../../../models/primitive/text.model';
import { TimeModel } from '../../../models/primitive/time.model';
import { TreeViewModel } from '../../../models/primitive/tree-view.model';
import { BaseInitializationService } from '../../../services/base-initialization/base-initialization.service';
import { assignModelOptions } from '../../../utilities/assign-model-options';
import { OutmodelComponent } from '../outmodel/outmodel.component';
import { createTreeViewData } from './create-tree-view-data';
import { RatingModel } from '../../../models/primitive/rating.model';
import { RatingModelOptions } from '../../../models/options/rating-model-options';
import { PCHAlertService } from '../../../../../../../apps/pace/src/app/core/services/pch-alerts/pch-alerts.service';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import { NotificationIdentifier } from '../../../types/notification-identifier';

@Component({
    selector: 'pace-core-widgets',
    templateUrl: './widgets.component.html',
    styleUrls: ['./widgets.component.scss'],
})
export class WidgetsComponent implements OnInit, OnDestroy {
    public appReinitializationRunning = false;
    private appReinitSubscription?: Subscription;
    public serviceReinitializationRunning = false;
    private serviceReinitSubscription?: Subscription;

    public widgetForm: FormGroup;
    public formChildren: FormArray;

    public textInputTest: TextModel;
    public textInputScannableTest: TextModel;
    public textInputUnitTest: TextModel;
    public textInputReadonlyTest: TextModel;
    public numberInputTest: NumberModel;
    public numberInputScannableTest: NumberModel;
    public numberStepperTest: NumberModel;
    public durationStepperTest: DurationModel;
    public durationStepperStepTest: DurationModel;
    public durationStepperUndefinedTest: DurationModel;
    public durationStepperMaxValueTest: DurationModel;
    public dateInputTest: DateModel;
    public timeInputTest: TimeModel;
    public dateTimeInputTest: TimeModel;
    public dropdownTest: DropdownModel<TextModel>;
    public dropdownInputTest: DropdownModel<TextModel>;
    public dropdownDisabledTest: DropdownModel<TextModel>;
    public dropdownMultiTest: DropdownModel<TextModel>;
    public radioInputTest: RadioModel<TextModel>;
    public checkboxInputTest: DisplayDecorator<BooleanModel>;
    public checkboxInputToggleTest: DisplayDecorator<BooleanModel>;
    public treeViewTest: TreeViewModel<TextModel>;
    public ratingInputTest: RatingModel;

    public alertTimeInSeconds = 10;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly formBuilder: FormBuilder,
        @Inject(InitializationInstanceToken) private readonly initializationService: BaseInitializationService,
        private readonly alert: PCHAlertService,
    ) {
        this.textInputTest = new TextModel(
            'TextInputTest',
            'Initial',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                pattern: 'Initial.*',
            }),
        );
        this.textInputScannableTest = new TextModel(
            'TextInputScannableTest',
            'Initial',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                scannable: true,
            }),
        );
        this.textInputUnitTest = new TextModel(
            'TextInputUnitTest',
            'Initial',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                pattern: 'Initial.*',
                scannable: true,
                unit: 'MHz',
            }),
        );
        this.textInputReadonlyTest = new TextModel(
            'TextInputReadonlyTest',
            'someValue',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                readonly: true,
            }),
        );

        const numberInitValue = 42;
        this.numberInputTest = new NumberModel(
            'NumberInputTest',
            undefined,
            new NumberModelOptions({
                minValue: 0,
                maxValue: 1000,
            }),
        );

        this.numberInputScannableTest = new NumberModel(
            'NumberInputScannableTest',
            numberInitValue,
            new NumberModelOptions({
                minValue: 0,
                maxValue: 1000,
                required: true,
                scannable: true,
                unit: 'KW',
            }),
        );

        this.ratingInputTest = new RatingModel(
            'RatingInputTest',
            '',
            new RatingModelOptions({ maxRatings: 10, required: true }),
        );

        this.numberStepperTest = new NumberModel(
            'NumberStepperTest',
            numberInitValue,
            new NumberModelOptions({
                minValue: 0,
                maxValue: 1000,
                step: 5,
            }),
        );

        this.durationStepperTest = new DurationModel(
            'DurationStepperTest',
            moment.duration(),
            new DurationModelOptions({}),
        );

        this.durationStepperStepTest = new DurationModel(
            'DurationStepperTest',
            moment.duration(),
            new DurationModelOptions({
                step: 5,
            }),
        );

        this.durationStepperUndefinedTest = new DurationModel('DurationStepperTest', undefined);

        this.durationStepperMaxValueTest = new DurationModel(
            'DurationMaxValueStepperTest',
            moment.duration('00:10'),
            new DurationModelOptions({
                minValue: moment.duration('00:05'),
                maxValue: moment.duration('01:30'),
            }),
        );

        this.dateInputTest = new DateModel(
            'DateInputTest',
            moment(),
            new DateModelOptions({
                minValue: moment('2024-01-01'),
                maxValue: moment('2025-12-31'),
            }),
        );
        this.timeInputTest = new TimeModel(
            'TimeInputTest',
            moment(),
            new TimeModelOptions({
                minValue: moment().startOf('hour'),
                maxValue: moment().endOf('day'),
            }),
        );

        this.dateTimeInputTest = new TimeModel(
            'DateTimeInputTest',
            moment(),
            new TimeModelOptions({
                minValue: moment('2024-01-01').startOf('hour'),
                maxValue: moment('2025-12-31').endOf('day'),
            }),
        );

        const dropdownOptions = [
            new DisplayDecorator<TextModel>('First entry', new TextModel('FirstEntry', 'first')),
            new DisplayDecorator<TextModel>('Second entry', new TextModel('SecondEntry', 'second')),
            new DisplayDecorator<TextModel>('Third entry', new TextModel('ThirdEntry', 'third')),
            new DisplayDecorator<TextModel>('Fourth entry', new TextModel('FourthEntry', 'fourth')),
        ];
        this.dropdownTest = new DropdownModel<TextModel>(
            'DropdownTest',
            dropdownOptions,
            new DropdownModelOptions({
                required: true,
                maxSelections: 1,
                scannable: true,
            }),
        );
        this.dropdownTest.selectByIndex(0);
        this.dropdownInputTest = new DropdownModel<TextModel>(
            'DropdownTest',
            dropdownOptions,
            new DropdownModelOptions({
                required: true,
                maxSelections: 1,
                scannable: true,
                displayText: true,
            }),
        );
        this.dropdownDisabledTest = new DropdownModel<TextModel>(
            'DropdownDisabledTest',
            dropdownOptions,
            new DropdownModelOptions({
                maxSelections: 1,
                disabled: true,
                scannable: true,
            }),
        );
        this.dropdownDisabledTest.selectByIndex(0);
        this.dropdownMultiTest = new DropdownModel<TextModel>(
            'DropdownTest',
            dropdownOptions,
            new DropdownModelOptions({
                scannable: false,
                required: true,
                maxSelections: 3,
            }),
        );
        this.dropdownMultiTest.selectByIndex(0);
        const radioOptions = [
            new DisplayDecorator<TextModel>('first', new TextModel('FirstRadio', 'firstRadio')),
            new DisplayDecorator<TextModel>('second', new TextModel('SecondRadio', 'secondRadio')),
            new DisplayDecorator<TextModel>('invalid', new TextModel('InvalidRadio', 'invalidRadio')),
        ];

        this.radioInputTest = new RadioModel<TextModel>(
            'RadioInputTest',
            radioOptions,
            new RadioModelOptions({
                required: true,
                initialSelection: 0,
                invalidSelection: 2,
            }),
        );
        this.radioInputTest.selectByIndex(0);

        this.checkboxInputTest = new DisplayDecorator<BooleanModel>(
            'Checkbox selected?',
            new BooleanModel(
                'BooleanInputTest',
                true,
                new BooleanModelOptions({
                    required: true,
                    showLabel: true,
                }),
            ),
        );

        this.checkboxInputToggleTest = new DisplayDecorator<BooleanModel>(
            'Enable/Disable inputs?',
            new BooleanModel(
                'ToggleInputTest',
                true,
                new BooleanModelOptions({
                    showLabel: false,
                }),
            ),
        );

        const treeViewRoot = createTreeViewData();
        this.treeViewTest = new TreeViewModel<TextModel>(
            'TreeViewTest',
            treeViewRoot,
            new TreeViewModelOptions({
                required: true,
                maxSelections: 2,
            }),
        );
        this.treeViewTest.selectEntryByIdAndTitle('5', 'Child 5');
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.widgetForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
    }

    public ngOnDestroy(): void {
        if (typeof this.appReinitSubscription !== 'undefined') {
            this.appReinitSubscription.unsubscribe();
        }
        if (typeof this.serviceReinitSubscription !== 'undefined') {
            this.serviceReinitSubscription.unsubscribe();
        }
    }

    public showOutmodel(model: ODataModel): void {
        this.modalService.show(OutmodelComponent, {
            backdrop: 'static',
            class: 'modal-lg',
            initialState: {
                model: model,
                label: model.constructor.name || 'ODataModel',
            },
            ariaLabelledBy: 'out-modal-title',
        });
    }

    public startInitApp(): void {
        this.appReinitializationRunning = true;

        const modalInstance = this.modalService.show(InitializationProgressComponent, {
            backdrop: 'static',
            class: 'modal-md modal-dialog-centered',
            initialState: {
                progress$: this.initializationService.initMessages$,
            },
            ariaLabelledBy: 'initialization-progress-modal-title',
        });

        this.appReinitSubscription = modalInstance.content?.rendered$
            .pipe(mergeMap(() => this.initializationService.init()))
            .subscribe(() => {
                this.serviceReinitializationRunning = false;
            });
    }

    public startInitServices(): void {
        this.serviceReinitializationRunning = true;

        const modalInstance = this.modalService.show(InitializationProgressComponent, {
            backdrop: 'static',
            class: 'modal-md modal-dialog-centered',
            initialState: {
                progress$: this.initializationService.initMessages$,
            },
            ariaLabelledBy: 'initialization-progress-modal-title',
        });

        this.serviceReinitSubscription = modalInstance.content?.rendered$
            .pipe(mergeMap(() => this.initializationService.initServices()))
            .subscribe(() => {
                this.serviceReinitializationRunning = false;
            });
    }

    public onCheckboxInputToggleChange(): void {
        this.checkboxInputTest = assignModelOptions(this.checkboxInputTest, {
            ...this.checkboxInputTest.options,
            disabled: !this.checkboxInputToggleTest.value,
        });
        this.dateInputTest = assignModelOptions(this.dateInputTest, {
            ...this.dateInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.dropdownTest = assignModelOptions(this.dropdownTest, {
            ...this.dropdownTest.options,
            disabled: !this.checkboxInputToggleTest.value,
        });
        this.dropdownInputTest = assignModelOptions(this.dropdownInputTest, {
            ...this.dropdownInputTest.options,
            disabled: !this.checkboxInputToggleTest.value,
        });
        this.numberInputTest = assignModelOptions(this.numberInputTest, {
            ...this.numberInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.numberInputScannableTest = assignModelOptions(this.numberInputScannableTest, {
            ...this.numberInputScannableTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.numberStepperTest = assignModelOptions(this.numberStepperTest, {
            ...this.numberStepperTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.radioInputTest = assignModelOptions(this.radioInputTest, {
            ...this.radioInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        const newTextInputTestOptions: TextModelOptions = {
            ...this.textInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputTest = new TextModel(this.textInputTest.name, this.textInputTest.value, newTextInputTestOptions);
        const newTextInputScannableTestOptions: TextModelOptions = {
            ...this.textInputScannableTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputScannableTest = new TextModel(
            this.textInputScannableTest.name,
            this.textInputScannableTest.value,
            newTextInputScannableTestOptions,
        );
        const newTextInputReadonlyTestOptions: TextModelOptions = {
            ...this.textInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputTest = new TextModel(
            this.textInputReadonlyTest.name,
            this.textInputReadonlyTest.value,
            newTextInputReadonlyTestOptions,
        );
        const newTextInputUnitTestOptions: TextModelOptions = {
            ...this.textInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputTest = new TextModel(
            this.textInputUnitTest.name,
            this.textInputUnitTest.value,
            newTextInputUnitTestOptions,
        );
        this.timeInputTest = assignModelOptions(this.timeInputTest, {
            ...this.timeInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
    }

    public onTextInputChange(event: ODataModelChangeEvent<TextModel>): void {
        this.log.debug(`Text input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onNumberInputChange(event: ODataModelChangeEvent<NumberModel>): void {
        this.log.debug(`Number input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onNumberStepperChange(event: ODataModelChangeEvent<NumberModel>): void {
        this.log.debug(`Number stepper input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDurationStepperChange(event: ODataModelChangeEvent<DurationModel>): void {
        this.log.debug(`Duration stepper input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDateInputChange(event: ODataModelChangeEvent<DateModel>): void {
        this.log.debug(`Date input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onTimeInputChange(event: ODataModelChangeEvent<TimeModel>): void {
        this.log.debug(`Time input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDateTimeInputChange(event: ODataModelChangeEvent<DateModel>): void {
        this.log.debug(`Date-time content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDropdownChange(event: ODataModelChangeEvent<DropdownModel<TextModel>>): void {
        this.log.debug(`Dropdown selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onRadioInputChange(event: ODataModelChangeEvent<RadioModel<TextModel>>): void {
        this.log.debug(`Radio input selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onCheckboxInputChange(event: ODataModelChangeEvent<BooleanModel>): void {
        this.log.debug(`Checkbox input selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onTreeviewInputChange(): void {
        this.log.debug(`TreeView selection changed (no info on selected not available)`);
    }

    public onRatingInputChange(event: ODataModelChangeEvent<RatingModel>): void {
        this.log.debug(`Rating input selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public addAlert(): void {
        const at = new Date(Date.now() + this.alertTimeInSeconds * 1000);
        const alert: NotificationIdentifier = {
            frontId: 'whocares',
            qmNum: getUuidV4(),
            maNum: getUuidV4(),
        };
        this.alert.addReminder(at, alert).pipe(take(1)).subscribe();
    }
}
